import { Cartesian2, Cartesian3, Color, PolylineDashMaterialProperty, } from "cesium";
import { CesiumUtils } from "../utilities/cesium-utils";
import { EarthBrainUtils, } from "../utilities/earthbrainutility";
import { createMetricDistanceUnitPort, } from "../settings/distance-unit";
import { formatDistance } from "../utilities/distance-format";
export class DistanceMeasurement {
    _distancePolylineEntities = [];
    _textEntities = [];
    _sphereEntities = [];
    _horizontalDistance;
    _verticalDistance;
    _directDistance;
    _start;
    _end;
    _site;
    _viewer;
    _distanceUnitPort = createMetricDistanceUnitPort();
    constructor(start, end, site, viewer, distanceUnitPort = createMetricDistanceUnitPort()) {
        this._start = start;
        this._end = end;
        this._site = site;
        this._viewer = viewer;
        this._distanceUnitPort = distanceUnitPort;
    }
    async update(start, end) {
        this.dispose();
        const color = Color.fromCssColorString("#FFC0CB");
        // const pointCloud = commandInputs.associatePointCloud;
        this._sphereEntities.push(EarthBrainUtils.addSphere(this._viewer, start, color), EarthBrainUtils.addSphere(this._viewer, end, color));
        // Calculating points in local space
        // const [localStart, localEnd] = pointCloud.transformPointsToLocalSpace([start, end]);
        const [localStart, localEnd] = await this._site.convertPointsToLocalSpace([
            start,
            end,
        ]);
        const topPoint = localStart.z > localEnd.z ? localStart : localEnd;
        const bottomPoint = localStart.z < localEnd.z ? localStart : localEnd;
        const rightAngledPointLocal = new Cartesian3(topPoint.x, topPoint.y, bottomPoint.z);
        this._directDistance = Cartesian3.distance(start, end);
        this._horizontalDistance = Cartesian3.distance(bottomPoint, rightAngledPointLocal);
        this._verticalDistance = Cartesian3.distance(topPoint, rightAngledPointLocal);
        const labelSettings = {
            showBackground: true,
            fontSize: 15,
            fillColor: Color.BLACK,
            backgroundColor: new Color(1, 1, 1, 1),
            backgroundPadding: new Cartesian2(7, 5),
            disableDepthDistance: Number.POSITIVE_INFINITY,
        };
        const lineSettings = {
            width: 1,
            color: color,
            clampToGround: false,
            outlineColor: color,
            outlineWidth: 0,
        };
        // Positioning texts at the center of each line
        const horizontalTextPosLocal = EarthBrainUtils.getDistantCartesianOnALine(rightAngledPointLocal, bottomPoint, this._horizontalDistance * 0.5);
        const verticalTextPosLocal = EarthBrainUtils.getDistantCartesianOnALine(rightAngledPointLocal, topPoint, this._verticalDistance * 0.5);
        const [horizontalTextPos, verticalTextPos, rightAngledPoint] = await this._site.convertPointsToWGS84([
            horizontalTextPosLocal,
            verticalTextPosLocal,
            rightAngledPointLocal,
        ]);
        const distTextPos = EarthBrainUtils.getDistantCartesianOnALine(start, end, this._directDistance * 0.5);
        this._textEntities.push(EarthBrainUtils.addLabel(this._viewer, distTextPos, formatDistance(this._distanceUnitPort, this._directDistance, 2), labelSettings));
        // const horizontalTextPos = pointCloud.transformPointToWorldSpace(
        //     EarthBrainUtils.getDistantCartesianOnALine(rightAngledPointLocal, bottomPoint, this._horizontalDistance * 0.5)
        // );
        this._textEntities.push(EarthBrainUtils.addLabel(this._viewer, horizontalTextPos, formatDistance(this._distanceUnitPort, this._horizontalDistance, 2), labelSettings));
        // const verticalTextPos = pointCloud.transformPointToWorldSpace(
        //     EarthBrainUtils.getDistantCartesianOnALine(rightAngledPointLocal, topPoint, this._verticalDistance * 0.5)
        // );
        this._textEntities.push(EarthBrainUtils.addLabel(this._viewer, verticalTextPos, formatDistance(this._distanceUnitPort, this._verticalDistance, 2), labelSettings));
        // const rightAngledPoint = pointCloud.transformPointToWorldSpace(rightAngledPointLocal);
        this._distancePolylineEntities.push(EarthBrainUtils.createLineEntity(this._viewer, "startend", [start, end], lineSettings));
        // This line is dashed
        const dashedLine = CesiumUtils.createPolyLine([start, rightAngledPoint, end], "start-mid-end", 1, new PolylineDashMaterialProperty({ color: color, dashLength: 10 }), false);
        this._viewer.entities.add(dashedLine);
        this._distancePolylineEntities.push(dashedLine);
        // Right angle marker
        const angleMarkerSize = Math.min(this._horizontalDistance, this._verticalDistance);
        const p1 = rightAngledPointLocal;
        const p2 = EarthBrainUtils.getDistantCartesianOnALine(rightAngledPointLocal, topPoint, angleMarkerSize * 0.1);
        const p4 = EarthBrainUtils.getDistantCartesianOnALine(rightAngledPointLocal, bottomPoint, angleMarkerSize * 0.1);
        const p3 = new Cartesian3(p4.x, p4.y, p2.z);
        const points = await this._site.convertPointsToWGS84([p1, p2, p3, p4]);
        // const points = [p1, p2, p3, p4].map(p => pointCloud.transformPointToWorldSpace(p));
        const rightAngleEntity = EarthBrainUtils.createLineEntity(this._viewer, "right-angle-marker", points, lineSettings);
        this._distancePolylineEntities.push(rightAngleEntity);
    }
    getData() {
        return {
            vertDist: this._verticalDistance,
            horDist: this._horizontalDistance,
            directDist: this._directDistance,
        };
    }
    updateVisibility(val) {
        this._distancePolylineEntities.forEach((d) => (d.show = val));
        this._textEntities.forEach((d) => (d.show = val));
        this._sphereEntities.forEach((d) => (d.show = val));
    }
    zoomTo() {
        this._distancePolylineEntities.length > 0 &&
            this._viewer.zoomTo(this._distancePolylineEntities[0]);
    }
    dispose() {
        this._distancePolylineEntities.forEach((e) => this._viewer.entities.remove(e));
        this._distancePolylineEntities.length = 0;
        this._textEntities.forEach((e) => this._viewer.entities.remove(e));
        this._textEntities.length = 0;
        this._sphereEntities.forEach((e) => this._viewer.entities.remove(e));
        this._sphereEntities.length = 0;
        this._verticalDistance = undefined;
        this._horizontalDistance = undefined;
        this._directDistance = undefined;
    }
    async recalculate() {
        await this.update(this._start, this._end);
        return;
    }
    async updateUnit(unitPort) {
        this._distanceUnitPort = unitPort;
        await this.recalculate();
    }
}
//# sourceMappingURL=distance-measurement.js.map